/**
 * AJ2 · Secciones de video de la home
 *
 *  <aj2-ugc>    «Asi se aplica en casa» (sections/aj2-home-ugc.liquid).
 *               Fila de reels con ficha. Suena como mucho uno a la vez; el
 *               primero visible arranca solo, en silencio, cuando la seccion
 *               entra en pantalla. En movil arranca el del centro al soltar
 *               el scroll y se pausa tocando el video.
 *  <aj2-farma>  «La farmaceutica responde» (sections/aj2-home-farma.liquid).
 *               Reparte lo que pinta cada bloque (reproductor, ficha y
 *               miniatura) y cambia los tres a la vez al elegir un video.
 *  <aj2-quick-add-component>
 *               El quick add de Horizon fuera de una product-card: Horizon
 *               saca la URL del producto de la tarjeta; aqui va en
 *               data-product-url. Se usa en los reels con variantes.
 *
 * Mejora progresiva: sin JS se ven los posters, las fichas enlazan al
 * producto y el boton de los reels con variantes navega a la ficha.
 * Con prefers-reduced-motion nada arranca solo.
 */
import { QuickAddComponent } from '@theme/quick-add';

const movimientoReducido = window.matchMedia('(prefers-reduced-motion: reduce)');
const esMovil = window.matchMedia('(max-width: 749px)');

class Aj2QuickAdd extends QuickAddComponent {
  get productPageUrl() {
    return this.dataset.productUrl || '';
  }
}
if (!customElements.get('aj2-quick-add-component')) {
  customElements.define('aj2-quick-add-component', Aj2QuickAdd);
}

/* ---------- utilidades de video ---------- */

function reproducir(video) {
  if (!video) return;
  const promesa = video.play();
  if (promesa && typeof promesa.catch === 'function') promesa.catch(() => {});
}

/** Enlaza progreso, estado y etiqueta del boton de play de un video. */
function enlazarVideo(video, raiz, signal) {
  const boton = raiz.querySelector('[data-aj2-play]');
  const etiqueta = (sonando) => {
    if (!boton) return;
    boton.setAttribute('aria-label', sonando ? boton.dataset.etiquetaPausar : boton.dataset.etiquetaReproducir);
  };
  video.addEventListener(
    'timeupdate',
    () => {
      const duracion = video.duration;
      const avance = duracion ? Math.min(1, video.currentTime / duracion) : 0;
      raiz.style.setProperty('--aj2-avance', String(avance));
    },
    { signal }
  );
  video.addEventListener(
    'play',
    () => {
      raiz.classList.add('is-reproduciendo');
      etiqueta(true);
    },
    { signal }
  );
  for (const evento of ['pause', 'ended']) {
    video.addEventListener(
      evento,
      () => {
        raiz.classList.remove('is-reproduciendo');
        etiqueta(false);
      },
      { signal }
    );
  }
}

function pintarSonido(raiz, conSonido) {
  if (!raiz) return;
  raiz.classList.toggle('is-sonido', conSonido);
  const boton = raiz.querySelector('[data-aj2-sonido]');
  if (!boton) return;
  boton.setAttribute('aria-pressed', String(conSonido));
  boton.setAttribute('aria-label', conSonido ? boton.dataset.etiquetaQuitar : boton.dataset.etiquetaPoner);
}

/* ==========================================================================
   <aj2-ugc>
   ========================================================================== */
class Aj2Ugc extends HTMLElement {
  connectedCallback() {
    /* trampa 26: el editor vuelve a llamar a connectedCallback al mover la
       seccion. Se desmonta lo anterior y se monta de cero. */
    this.controlador?.abort();
    this.observador?.disconnect();
    this.controlador = new AbortController();
    const { signal } = this.controlador;

    this.pista = this.querySelector('[data-aj2-pista]');
    if (!this.pista) return;
    this.reels = [...this.pista.querySelectorAll('[data-aj2-reel]')];
    this.anterior = this.querySelector('[data-aj2-anterior]');
    this.siguiente = this.querySelector('[data-aj2-siguiente]');
    this.barra = this.querySelector('[data-aj2-barra]');
    this.activo = null;
    this.enVista = false;
    this.pausadoAMano = false;

    this.anterior?.addEventListener('click', () => this.mover(-1), { signal });
    this.siguiente?.addEventListener('click', () => this.mover(1), { signal });

    let espera = 0;
    this.pista.addEventListener(
      'scroll',
      () => {
        this.pintarNavegacion();
        clearTimeout(espera);
        espera = window.setTimeout(() => this.alSoltarScroll(), 160);
      },
      { passive: true, signal }
    );
    window.addEventListener('resize', () => this.pintarNavegacion(), { passive: true, signal });

    for (const reel of this.reels) {
      const video = reel.querySelector('video');
      if (!video) continue;
      enlazarVideo(video, reel, signal);
      reel.querySelector('[data-aj2-tocar]')?.addEventListener('click', () => this.alternar(reel), { signal });
      reel.querySelector('[data-aj2-play]')?.addEventListener('click', () => this.alternar(reel), { signal });
      reel.querySelector('[data-aj2-sonido]')?.addEventListener('click', () => this.sonido(reel), { signal });
    }

    this.observador = new IntersectionObserver(
      ([entrada]) => {
        this.enVista = entrada.isIntersecting;
        if (this.enVista) this.autoarrancar();
        else this.videoDe(this.activo)?.pause();
      },
      { threshold: 0.35 }
    );
    this.observador.observe(this.pista);
    this.pintarNavegacion();
  }

  disconnectedCallback() {
    this.controlador?.abort();
    this.observador?.disconnect();
    this.videoDe(this.activo)?.pause();
  }

  videoDe(reel) {
    return reel ? reel.querySelector('video') : null;
  }

  paso() {
    const primero = this.reels[0];
    if (!primero) return 0;
    const hueco = parseFloat(getComputedStyle(this.pista).columnGap) || 0;
    return primero.getBoundingClientRect().width + hueco;
  }

  mover(sentido) {
    this.pista.scrollBy({
      left: sentido * this.paso(),
      behavior: movimientoReducido.matches ? 'auto' : 'smooth',
    });
  }

  pintarNavegacion() {
    const { scrollLeft, scrollWidth, clientWidth } = this.pista;
    const maximo = Math.max(0, scrollWidth - clientWidth);
    if (this.anterior) this.anterior.disabled = scrollLeft <= 2;
    if (this.siguiente) this.siguiente.disabled = scrollLeft >= maximo - 2;
    if (this.barra) {
      const ancho = scrollWidth > 0 ? Math.min(1, clientWidth / scrollWidth) : 1;
      const avance = maximo > 0 ? scrollLeft / maximo : 0;
      this.barra.style.width = `${ancho * 100}%`;
      this.barra.style.left = `${avance * (1 - ancho) * 100}%`;
    }
  }

  /** Cuanto de un reel se ve dentro de la fila (0-1). */
  visible(reel) {
    const caja = this.pista.getBoundingClientRect();
    const r = reel.getBoundingClientRect();
    const dentro = Math.min(r.right, caja.right) - Math.max(r.left, caja.left);
    return r.width ? Math.max(0, dentro) / r.width : 0;
  }

  /** En movil, el reel del centro; en escritorio, el primero de la fila. */
  candidato() {
    const caja = this.pista.getBoundingClientRect();
    const inicio = caja.left + (this.reels[0] ? this.reels[0].offsetLeft : 0);
    let mejor = null;
    let distancia = Infinity;
    for (const reel of this.reels) {
      if (!this.videoDe(reel)) continue;
      const r = reel.getBoundingClientRect();
      const d = esMovil.matches
        ? Math.abs(r.left + r.width / 2 - (caja.left + caja.width / 2))
        : Math.abs(r.left - inicio);
      if (d < distancia) {
        distancia = d;
        mejor = reel;
      }
    }
    return mejor;
  }

  autoarrancar() {
    if (movimientoReducido.matches || this.pausadoAMano) return;
    const reel = this.activo && this.visible(this.activo) >= 0.6 ? this.activo : this.candidato();
    if (reel) this.arrancar(reel);
  }

  alSoltarScroll() {
    if (!this.enVista) return;
    if (esMovil.matches) {
      const reel = this.candidato();
      if (reel && reel !== this.activo) {
        this.pausadoAMano = false;
        this.autoarrancar();
      }
      return;
    }
    if (this.activo && this.visible(this.activo) < 0.6) {
      this.soltar(this.activo);
      this.activo = null;
      this.autoarrancar();
    }
  }

  arrancar(reel) {
    if (this.activo && this.activo !== reel) this.soltar(this.activo);
    this.activo = reel;
    reel.classList.add('is-activo');
    reproducir(this.videoDe(reel));
  }

  soltar(reel) {
    const video = this.videoDe(reel);
    if (video) {
      video.pause();
      video.muted = true;
    }
    reel.classList.remove('is-activo');
    pintarSonido(reel, false);
  }

  alternar(reel) {
    const video = this.videoDe(reel);
    if (!video) return;
    if (reel === this.activo && !video.paused) {
      video.pause();
      this.pausadoAMano = true;
    } else {
      this.pausadoAMano = false;
      this.arrancar(reel);
    }
  }

  sonido(reel) {
    const video = this.videoDe(reel);
    if (!video) return;
    if (reel !== this.activo || video.paused) {
      this.pausadoAMano = false;
      this.arrancar(reel);
    }
    video.muted = !video.muted;
    pintarSonido(reel, !video.muted);
  }
}

if (!customElements.get('aj2-ugc')) customElements.define('aj2-ugc', Aj2Ugc);

/* ==========================================================================
   <aj2-farma>
   ========================================================================== */
class Aj2Farma extends HTMLElement {
  connectedCallback() {
    this.controlador?.abort();
    this.observador?.disconnect();
    this.controlador = new AbortController();
    const { signal } = this.controlador;

    this.escena = this.querySelector('[data-aj2-farma-escena]');
    this.fichas = this.querySelector('[data-aj2-farma-fichas]');
    this.rail = this.querySelector('[data-aj2-farma-rail]');
    this.mas = this.querySelector('[data-aj2-farma-mas]');
    const fuente = this.querySelector('[data-aj2-farma-fuente]');
    if (!this.escena || !this.fichas || !this.rail || !fuente) return;

    /* Montaje: cada bloque trae sus tres piezas juntas (un bloque solo se
       pinta una vez, trampa 4). Se reparten en orden, asi el indice de un
       video es el mismo en las tres listas. */
    for (const item of [...fuente.querySelectorAll('[data-aj2-farma-item]')]) {
      const jugador = item.querySelector('[data-aj2-farma-player]');
      const ficha = item.querySelector('[data-aj2-farma-ficha]');
      const mini = item.querySelector('[data-aj2-farma-mini]');
      if (!jugador || !ficha || !mini) continue;
      this.escena.appendChild(jugador);
      this.fichas.appendChild(ficha);
      this.rail.appendChild(mini);
      item.remove();
    }
    this.jugadores = [...this.escena.querySelectorAll('[data-aj2-farma-player]')];
    this.listaFichas = [...this.fichas.querySelectorAll('[data-aj2-farma-ficha]')];
    this.minis = [...this.rail.querySelectorAll('[data-aj2-farma-mini]')];
    this.setAttribute('data-montado', '');
    if (this.mas) this.mas.hidden = this.minis.length < 2;
    if (!this.jugadores.length) return;

    this.indice = -1;
    this.conSonido = false;
    this.pausadoAMano = false;
    this.enVista = false;

    this.minis.forEach((mini, i) => mini.addEventListener('click', () => this.activar(i, true), { signal }));
    for (const jugador of this.jugadores) {
      const video = jugador.querySelector('video');
      if (!video) continue;
      enlazarVideo(video, jugador, signal);
      jugador.querySelector('[data-aj2-tocar]')?.addEventListener('click', () => this.alternar(), { signal });
      jugador.querySelector('[data-aj2-play]')?.addEventListener('click', () => this.alternar(), { signal });
      jugador.querySelector('[data-aj2-sonido]')?.addEventListener('click', () => this.sonido(), { signal });
    }
    this.activar(0, false);

    this.observador = new IntersectionObserver(
      ([entrada]) => {
        this.enVista = entrada.isIntersecting;
        const video = this.video();
        if (!video) return;
        if (!this.enVista) video.pause();
        else if (!movimientoReducido.matches && !this.pausadoAMano) reproducir(video);
      },
      { threshold: 0.4 }
    );
    this.observador.observe(this.escena);

    /* En el editor, elegir un bloque lo pone en escena. */
    document.addEventListener(
      'shopify:block:select',
      (evento) => {
        const i = this.minis.findIndex((mini) => mini.dataset.bloque === evento.detail?.blockId);
        if (i >= 0) this.activar(i, false);
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.controlador?.abort();
    this.observador?.disconnect();
    this.video()?.pause();
  }

  video() {
    return this.jugadores?.[this.indice]?.querySelector('video') || null;
  }

  activar(i, porUsuario) {
    if (i === this.indice) {
      if (porUsuario) this.alternar();
      return;
    }
    const anterior = this.video();
    if (anterior) {
      anterior.pause();
      anterior.muted = true;
      try {
        anterior.currentTime = 0;
      } catch (error) {
        /* sin metadatos todavia: no hay nada que rebobinar */
      }
    }
    this.indice = i;
    this.jugadores.forEach((jugador, k) => {
      jugador.hidden = k !== i;
    });
    this.listaFichas.forEach((ficha, k) => {
      ficha.hidden = k !== i;
    });
    this.minis.forEach((mini, k) => mini.setAttribute('aria-current', String(k === i)));
    this.jugadores.forEach((jugador) => pintarSonido(jugador, this.conSonido));

    const video = this.video();
    if (porUsuario) this.centrarMini(i);
    if (!video) return;
    video.muted = !this.conSonido;
    if (porUsuario) {
      this.pausadoAMano = false;
      reproducir(video);
    } else if (this.enVista && !movimientoReducido.matches && !this.pausadoAMano) {
      reproducir(video);
    }
  }

  /** Lleva la miniatura elegida a la vista dentro de la tira, sin mover la pagina. */
  centrarMini(i) {
    const mini = this.minis[i];
    if (!mini || this.rail.scrollWidth <= this.rail.clientWidth) return;
    const destino = mini.offsetLeft - (this.rail.clientWidth - mini.offsetWidth) / 2;
    this.rail.scrollTo({ left: Math.max(0, destino), behavior: movimientoReducido.matches ? 'auto' : 'smooth' });
  }

  alternar() {
    const video = this.video();
    if (!video) return;
    if (video.paused) {
      this.pausadoAMano = false;
      reproducir(video);
    } else {
      video.pause();
      this.pausadoAMano = true;
    }
  }

  sonido() {
    const video = this.video();
    if (!video) return;
    this.conSonido = !this.conSonido;
    video.muted = !this.conSonido;
    if (this.conSonido && video.paused) {
      this.pausadoAMano = false;
      reproducir(video);
    }
    this.jugadores.forEach((jugador) => pintarSonido(jugador, this.conSonido));
  }
}

if (!customElements.get('aj2-farma')) customElements.define('aj2-farma', Aj2Farma);
